import { GenericEvent } from '@central-products/transport-bus-common';
import { getServiceAlreadyBoundErrorMsg } from '../errorMessages';
import { BindMethodArgs, BindServiceArgs, MethodBinding, ServiceBinding, ServicePath } from './types';
import { callMethod, convertResultToResponse } from './utils';

const serviceBindings = new Map<string, ServiceBinding>();

const getBindingKey = ({ domain, serviceName }: ServicePath) => `${domain}:${serviceName}`;

export function getServiceBinding(path: ServicePath): ServiceBinding | undefined {
  const key = getBindingKey(path);
  return serviceBindings.get(key);
}

function createMethodBinding({ implementation, domain, serviceName, method }: BindMethodArgs): MethodBinding {
  return ({ payload }: GenericEvent) => {
    const implementationMethod = implementation[method.property].bind(implementation);

    return callMethod(implementationMethod, payload)
      .then(convertResultToResponse(method, domain, serviceName))
      .catch((error: any) => {
        const result = convertResultToResponse(method, domain, serviceName)(error);
        return Promise.reject(result);
      });
  };
}

function createServiceMethodsBinding(args: BindServiceArgs): Map<string, MethodBinding> {
  const { implementation, domain, serviceName, methods } = args;
  const methodBindings = methods.map(
    (method): [string, MethodBinding] => [
      method.name,
      createMethodBinding({ implementation, domain, serviceName, method }),
    ]
  );

  return new Map(methodBindings);
}

export function bindService(args: BindServiceArgs): void {
  const { implementation, domain, serviceName } = args;
  const key = getBindingKey({ domain, serviceName });

  if (serviceBindings.has(key)) {
    throw new Error(getServiceAlreadyBoundErrorMsg(serviceName));
  }

  serviceBindings.set(key, {
    implementation,
    methods: createServiceMethodsBinding(args),
  });
}

export function unbindService(path: ServicePath): void {
  const key = getBindingKey(path);
  serviceBindings.delete(key);
}
